import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  FaRegCopy,
  FaCheck,
  FaEllipsisV,
  FaArchive,
  FaUndo,
  FaExternalLinkAlt,
  FaUserPlus,
} from 'react-icons/fa';
import Loader from '../../Loader';
import { getEnquiries, updateEnquiryStatus } from '../../../services/axios';
import { DEVELOPER_API, READER_APP } from '../../../services/entitlements';
import { ROUTES } from '../../../assets/constants/strings';

// Human-readable label for the product an enquiry came from.
const productLabel = (p) =>
  p === DEVELOPER_API
    ? 'Developer API'
    : p === READER_APP
    ? 'RegulationCity App'
    : p || '—';

const mapStateToProps = (state) => ({
  token: state.user.user ? state.user.user.token : null,
});

const connector = connect(mapStateToProps);

// Inbox workflow states. Pre-existing rows with no status are treated as 'New'.
const STATUS_NEW = 'New';
const STATUS_HANDLED = 'Handled';
const STATUS_ARCHIVED = 'Archived';
const normStatus = (s) => s || STATUS_NEW;

// Tabs across the top of the inbox; 'all' shows everything.
const FILTERS = [
  { key: STATUS_NEW, label: 'New' },
  { key: STATUS_HANDLED, label: 'Handled' },
  { key: STATUS_ARCHIVED, label: 'Archived' },
  { key: 'all', label: 'All' },
];

// Admin inbox of API enquiries (contact form + "request more calls"). Newest first, with a
// per-enquiry workflow: New → Handled/Archived, and back. Status changes persist server-side.
const Enquiries = ({ token }) => {
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [filter, setFilter] = useState(STATUS_NEW);
  const [expanded, setExpanded] = useState({}); // { [id]: true } — message shown in full
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState(null);
  const [menu, setMenu] = useState(null); // { id, top, left } — open row action menu (fixed-positioned)
  const menuRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchRows = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const res = await getEnquiries(token);
      if (cancelled) return;
      const list = Array.isArray(res) ? res : [];
      // Show enquiries for BOTH products — reader Enterprise leads land here too
      // (they post Product=ReaderApp). The Product column tells them apart.
      list.sort((a, b) => new Date(b.created) - new Date(a.created));
      setRows(list);
      setIsLoading(false);
    };
    fetchRows();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const copyEmail = (email) => {
    if (!email) return;
    navigator.clipboard.writeText(email).then(() => {
      setCopied(email);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const MENU_WIDTH = 208;
  const closeMenu = () => setMenu(null);
  const openMenu = (e, enquiry) => {
    e.stopPropagation();
    if (menu && menu.id === enquiry.id) {
      closeMenu();
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    // Align the menu's right edge under the trigger; clamp so it never runs off the left.
    setMenu({ id: enquiry.id, top: r.bottom + 6, left: Math.max(12, r.right - MENU_WIDTH) });
  };

  // The menu is position:fixed (to escape the table's overflow clip), so dismiss it on any
  // outside click, scroll or resize — otherwise it would float detached from its row.
  useEffect(() => {
    if (!menu) return undefined;
    const onDocClick = (ev) => {
      if (menuRef.current && menuRef.current.contains(ev.target)) return;
      closeMenu();
    };
    const onDismiss = () => closeMenu();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('scroll', onDismiss, true); // capture: catch the inner content scroll too
    window.addEventListener('resize', onDismiss);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('scroll', onDismiss, true);
      window.removeEventListener('resize', onDismiss);
    };
  }, [menu]);

  // Optimistically move the row's status, persist, and roll back on failure.
  const changeStatus = async (enquiry, next) => {
    if (!token || updatingId) return;
    closeMenu();
    const prev = normStatus(enquiry.status);
    setUpdatingId(enquiry.id);
    setError(null);
    setRows((rs) => rs.map((r) => (r.id === enquiry.id ? { ...r, status: next } : r)));
    const res = await updateEnquiryStatus(token, enquiry.id, next);
    setUpdatingId(null);
    if (!res || !res.success) {
      setRows((rs) => rs.map((r) => (r.id === enquiry.id ? { ...r, status: prev } : r)));
      setError((res && res.reason) || 'Could not update the enquiry.');
    }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt)
      ? '—'
      : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
          ', ' +
          dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const counts = rows.reduce(
    (acc, e) => {
      acc[normStatus(e.status)] = (acc[normStatus(e.status)] || 0) + 1;
      acc.all += 1;
      return acc;
    },
    { all: 0, [STATUS_NEW]: 0, [STATUS_HANDLED]: 0, [STATUS_ARCHIVED]: 0 }
  );

  const visible = rows.filter((e) => filter === 'all' || normStatus(e.status) === filter);

  const statusBadge = (status) => {
    const s = normStatus(status);
    const mod =
      s === STATUS_NEW
        ? 'adm-status--new'
        : s === STATUS_HANDLED
        ? 'adm-status--handled'
        : 'adm-status--archived';
    return <span className={`adm-status ${mod}`}>{s}</span>;
  };

  // The workflow actions a row offers, given its current status. Each becomes an item in the
  // row's kebab menu. 'lookUp' is a navigation action, kept below a divider.
  const menuItemsFor = (e) => {
    const s = normStatus(e.status);
    const items = [];
    if (s !== STATUS_HANDLED) {
      items.push({ key: 'handled', icon: <FaCheck />, label: 'Mark handled', onClick: () => changeStatus(e, STATUS_HANDLED) });
    }
    if (s === STATUS_HANDLED || s === STATUS_ARCHIVED) {
      items.push({ key: 'reopen', icon: <FaUndo />, label: 'Reopen', onClick: () => changeStatus(e, STATUS_NEW) });
    }
    if (s !== STATUS_ARCHIVED) {
      items.push({ key: 'archive', icon: <FaArchive />, label: 'Archive', onClick: () => changeStatus(e, STATUS_ARCHIVED) });
    }
    if (e.email) {
      // Open the provisioning wizard pre-filled from this enquiry; a successful provision marks it
      // Handled. Product maps straight through ('ReaderApp' / 'DeveloperApi').
      items.push({
        key: 'provision',
        icon: <FaUserPlus />,
        label: 'Provision customer',
        divider: true,
        onClick: () => {
          closeMenu();
          history.push({
            pathname: `${ROUTES.APIAdmin}/provision-trial`,
            state: {
              prefill: {
                firstName: e.firstName || '',
                lastName: e.lastName || '',
                email: e.email,
                company: e.organisation || '',
                product: e.product,
                enquiryId: e.id,
              },
            },
          });
        },
      });
      items.push({
        key: 'lookup',
        icon: <FaExternalLinkAlt />,
        label: 'Look up customer',
        onClick: () => {
          closeMenu();
          history.push(`${ROUTES.APIAdmin}/customers?email=${encodeURIComponent(e.email)}`);
        },
      });
    }
    return items;
  };

  const menuEnquiry = menu ? rows.find((r) => r.id === menu.id) : null;

  return (
    <section className='adm-section'>
      <p className='adm-sub'>
        Contact-form and "request more calls" submissions across both products, newest first. Mark
        each one handled or archived as you work through them.
      </p>

      <div className='adm-tabs' role='tablist'>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type='button'
            role='tab'
            aria-selected={filter === f.key}
            className={`adm-tab${filter === f.key ? ' is-active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            <span className='adm-tab__count'>{counts[f.key] || 0}</span>
          </button>
        ))}
      </div>

      {error && (
        <div className='adm-note' style={{ color: '#dc3545', fontWeight: 500, marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {isLoading ? (
        <div className='adm-panel'>
          <Loader />
        </div>
      ) : visible.length === 0 ? (
        <div className='adm-empty'>
          {filter === STATUS_NEW
            ? 'No new enquiries — you’re all caught up.'
            : `No ${filter === 'all' ? '' : filter.toLowerCase() + ' '}enquiries.`}
        </div>
      ) : (
        <div className='adm-panel adm-panel--flush'>
          <div className='adm-tablewrap'>
            <table className='adm-table'>
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Status</th>
                  <th>Name</th>
                  <th>Product</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Message</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((e) => {
                  const msg = e.message || '';
                  const isLong = msg.length > 140;
                  const isOpen = !!expanded[e.id];
                  return (
                    <tr key={e.id} className={updatingId === e.id ? 'adm-row--busy' : ''}>
                      <td className='adm-table__nowrap adm-table__muted'>{fmtDate(e.created)}</td>
                      <td className='adm-table__nowrap'>{statusBadge(e.status)}</td>
                      <td className='adm-table__nowrap'>
                        {`${e.firstName || ''} ${e.lastName || ''}`.trim() || '—'}
                      </td>
                      <td className='adm-table__nowrap'>{productLabel(e.product)}</td>
                      <td className='adm-table__nowrap'>
                        {e.email ? (
                          <span className='adm-inline'>
                            <a href={`mailto:${e.email}`}>{e.email}</a>
                            <button
                              type='button'
                              className={`adm-iconbtn${copied === e.email ? ' is-done' : ''}`}
                              title={copied === e.email ? 'Copied' : 'Copy email'}
                              aria-label='Copy email'
                              onClick={() => copyEmail(e.email)}
                            >
                              {copied === e.email ? <FaCheck /> : <FaRegCopy />}
                            </button>
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className='adm-table__nowrap'>{e.organisation || '—'}</td>
                      <td className='adm-table__msg'>
                        {msg ? (
                          <>
                            <span className={!isOpen && isLong ? 'adm-msg--clamp' : ''}>{msg}</span>
                            {isLong && (
                              <button
                                type='button'
                                className='adm-linkbtn'
                                onClick={() => toggleExpand(e.id)}
                              >
                                {isOpen ? 'Show less' : 'Show more'}
                              </button>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className='adm-table__nowrap adm-table__actions'>
                        <button
                          type='button'
                          className={`adm-kebab${menu && menu.id === e.id ? ' is-open' : ''}`}
                          aria-label='Enquiry actions'
                          aria-haspopup='true'
                          aria-expanded={menu && menu.id === e.id ? 'true' : 'false'}
                          disabled={updatingId === e.id}
                          onClick={(ev) => openMenu(ev, e)}
                        >
                          <FaEllipsisV />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {menu && menuEnquiry && (
        <div
          ref={menuRef}
          className='adm-menu'
          role='menu'
          style={{ top: menu.top, left: menu.left, width: MENU_WIDTH }}
        >
          {menuItemsFor(menuEnquiry).map((item) => (
            <React.Fragment key={item.key}>
              {item.divider && <div className='adm-menu__divider' />}
              <button type='button' role='menuitem' className='adm-menu__item' onClick={item.onClick}>
                <span className='adm-menu__icon'>{item.icon}</span>
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </section>
  );
};

export default connector(Enquiries);
